import { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { apiClient, User } from '@/lib/apiClient';
import { logoutFromKeycloak, KC_TOKEN_EXPIRY_KEY } from '@/lib/keycloak';
import { useNavigate } from 'react-router-dom';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  setAuthUser: (user: User) => void;
}

const AUTH_STORAGE_KEY = 'auth_user';
const DEFAULT_IDLE_TIMEOUT_MINUTES = 60;
const configuredIdleTimeoutMinutes = Number(import.meta.env.VITE_IDLE_TIMEOUT_MINUTES);
const IDLE_TIMEOUT_MS =
  Number.isFinite(configuredIdleTimeoutMinutes) && configuredIdleTimeoutMinutes > 0
    ? configuredIdleTimeoutMinutes * 60 * 1000
    : DEFAULT_IDLE_TIMEOUT_MINUTES * 60 * 1000;
const ACTIVITY_THROTTLE_MS = 1_000;
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'] as const;

const readStoredUser = (): User | null => {
  try {
    const raw = sessionStorage.getItem(AUTH_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
};

const writeStoredUser = (user: User | null) => {
  if (user) {
    sessionStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(user));
  } else {
    sessionStorage.removeItem(AUTH_STORAGE_KEY);
  }
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const storedUser = readStoredUser();
  const [user, setUser] = useState<User | null>(storedUser);
  const [isLoading, setIsLoading] = useState(!storedUser);
  const navigate = useNavigate();
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const lastThrottledRef = useRef<number>(0);

  // Check if user is authenticated on mount.
  // Skip on the OAuth callback route — the user is mid-authentication and the session
  // cookie isn't set yet, so the check would return 401 and trigger an unwanted redirect.
  useEffect(() => {
    if (window.location.pathname !== '/oauth/callback') {
      checkAuth();
    }
  }, []);

  // Listen for 401 responses from any API call and redirect to login.
  // Ignore the event on the OAuth callback route — a 401 there is expected (session cookie
  // isn't set yet) and the callback handler will navigate on its own after authentication.
  useEffect(() => {
    const handleUnauthorized = () => {
      if (window.location.pathname === '/oauth/callback') return;
      writeStoredUser(null);
      sessionStorage.removeItem(KC_TOKEN_EXPIRY_KEY);
      setUser(null);
      navigate('/', { replace: true });
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, [navigate]);

  // Proactive token refresh timer.
  // Runs whenever `user` changes: schedules a refresh 60s before the access token expires.
  // On tab focus (visibilitychange), refreshes immediately if within 60s of expiry.
  useEffect(() => {
    if (refreshTimerRef.current !== null) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    if (!user) return;

    // doRefresh and scheduleNext are mutually recursive; declare scheduleNext first.
    let refreshInFlight = false;
    // eslint-disable-next-line prefer-const
    let scheduleNext: (expiryMs: number) => void;

    const doRefresh = async () => {
      refreshTimerRef.current = null; // timer has fired; we are the active refresh
      if (refreshInFlight) return;

      // Skip refresh when the user has been idle — the idle logout timer will handle it.
      if (Date.now() - lastActivityRef.current > IDLE_TIMEOUT_MS) return;

      refreshInFlight = true;
      try {
        const { expiresIn } = await apiClient.refreshToken();
        const newExpiry = Date.now() + expiresIn * 1000;
        sessionStorage.setItem(KC_TOKEN_EXPIRY_KEY, String(newExpiry));
        scheduleNext(newExpiry);
      } catch {
        // Refresh token expired or invalid — force logout.
        // Guard against double-navigate: handleUnauthorized may have already run
        // if the refresh call returned 401 and triggered the auth:unauthorized event.
        writeStoredUser(null);
        sessionStorage.removeItem(KC_TOKEN_EXPIRY_KEY);
        if (user) {
          setUser(null);
          navigate('/', { replace: true });
        }
      } finally {
        refreshInFlight = false;
      }
    };

    scheduleNext = (expiryMs: number) => {
      if (refreshTimerRef.current !== null) {
        clearTimeout(refreshTimerRef.current);
      }
      const delay = Math.max(0, expiryMs - Date.now() - 60_000);
      refreshTimerRef.current = setTimeout(doRefresh, delay);
    };

    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const stored = sessionStorage.getItem(KC_TOKEN_EXPIRY_KEY);
      if (stored && Number(stored) - Date.now() < 60_000) {
        if (refreshTimerRef.current !== null) {
          clearTimeout(refreshTimerRef.current);
          refreshTimerRef.current = null;
        }
        doRefresh();
      }
    };

    const stored = sessionStorage.getItem(KC_TOKEN_EXPIRY_KEY);
    if (stored) {
      scheduleNext(Number(stored));
    } else {
      // New tab or sessionStorage was cleared — refresh immediately to establish timer
      doRefresh();
    }

    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      if (refreshTimerRef.current !== null) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [user, navigate]);

  // Idle logout: resets on every activity event and starts the countdown fresh.
  // Defined before the activity effect so the effect can reference it.
  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current !== null) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = null;
  }, []);

  // Activity tracker — only active while the user is logged in.
  // Listens to common interaction events, throttles updates to 1s, and resets
  // the idle logout timer on each detected activity burst.
  useEffect(() => {
    if (!user) return;
    lastActivityRef.current = Date.now();
    lastThrottledRef.current = 0;

    const startIdleCountdown = () => {
      if (idleTimerRef.current !== null) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        idleTimerRef.current = null;
        logout();
      }, IDLE_TIMEOUT_MS);
    };

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastThrottledRef.current < ACTIVITY_THROTTLE_MS) return;
      lastThrottledRef.current = now;
      lastActivityRef.current = now;
      startIdleCountdown(); // reset the 30-min countdown
    };

    ACTIVITY_EVENTS.forEach(evt => document.addEventListener(evt, handleActivity, { passive: true }));
    startIdleCountdown(); // begin the countdown on login / mount

    return () => {
      ACTIVITY_EVENTS.forEach(evt => document.removeEventListener(evt, handleActivity));
      if (idleTimerRef.current !== null) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    };
  }, [user]);

  const checkAuth = async () => {
    try {
      const profile = await apiClient.getCurrentUserProfile();

      // Auto-detect timezone on first login and persist it silently.
      const detectedTimezone = !profile.timezone
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : null;

      if (detectedTimezone) {
        try {
          await apiClient.updateUserSettings(profile.id, { timezone: detectedTimezone });
        } catch {
          // Non-fatal — the UI will fall back to browser timezone via useUserTimezone().
        }
      }

      const mappedUser: User = {
        id: profile.id,
        email: profile.email ?? '',
        name: profile.nickName ?? (`${profile.firstName ?? ''} ${profile.lastName ?? ''}`.trim() || ''),
        username: profile.nickName ?? undefined,
        firstName: profile.firstName ?? undefined,
        lastName: profile.lastName ?? undefined,
        theme: profile.theme ?? undefined,
        language: profile.language ?? undefined,
        timezone: profile.timezone ?? detectedTimezone ?? undefined,
      };
      writeStoredUser(mappedUser);
      setUser(mappedUser);
    } catch {
      writeStoredUser(null);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    // Cancel any pending timers immediately so they cannot fire during teardown.
    if (refreshTimerRef.current !== null) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    resetIdleTimer();
    try {
      await apiClient.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      writeStoredUser(null);
      sessionStorage.removeItem(KC_TOKEN_EXPIRY_KEY);
      setUser(null);
      // End the Keycloak SSO session so the user can choose a different IdP on
      // next login.  logoutFromKeycloak() redirects the browser to the Keycloak
      // logout endpoint which then redirects back to the app origin.
      logoutFromKeycloak();
    }
  };

  const setAuthUser = (u: User) => {
    writeStoredUser(u);
    setUser(u);
    setIsLoading(false);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    logout,
    refreshAuth: checkAuth,
    setAuthUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
